import { ApplicationConfig, inject, provideAppInitializer } from '@angular/core';
import { provideCorePersonalAccessTokenDataAccess } from '@maximizer/core/personal-access-token/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  OUTLOOK_SYNC_ENV,
  OutlookSyncOAuthFlowService,
  readPersistedHandshakeNonce,
} from '@maximizer/outlook-sync/data-access';
import {
  IS_BROWSER_ONLY_ROUTE,
  OAUTH2_PEER_TOKEN_BRIDGE,
  OAuth2Service,
  PopupSessionExpiredService,
} from '@maximizer/oauth2/data-access';
import {
  InitializationService,
  OutlookStore,
  requestHandshakeFromOpener,
  SYNC_CONFIG_HANDSHAKE_NONCE_PARAM,
} from '@maximizer/outlook/shared/data-access';
import { environment } from '../environments/environment';
import { outlookCoreProvidersBase } from './app.config';

export const syncConfigurationAppConfig: ApplicationConfig = {
  providers: [
    ...outlookCoreProvidersBase,
    provideAppInitializer(async () => {
      const oauth2Service = inject(OAuth2Service);
      const outlookStore = inject(OutlookStore);
      const sessionExpiredSignal = inject(PopupSessionExpiredService);
      const initializationService = inject(InitializationService);
      const oauthFlow = inject(OutlookSyncOAuthFlowService);
      const insightsService = inject(InsightsService);

      const isCloud = oauth2Service.getIsCloudFromStorage();
      outlookStore.setIsCloud(isCloud);
      if (isCloud) {
        insightsService.enable();
      } else {
        insightsService.disable();
      }

      try {
        const params = new URLSearchParams(window.location.search);
        const isOauthCallback =
          params.has('code') || (params.has('error') && params.has('state'));
        const callbackNonce = isOauthCallback
          ? readPersistedHandshakeNonce()
          : null;
        oauthFlow.captureHandshakeNonce(
          params.get(SYNC_CONFIG_HANDSHAKE_NONCE_PARAM) ?? callbackNonce,
        );
        if (!oauth2Service.requiredPropertiesPopulated) {
          const hydrated = isOauthCallback
            ? !!callbackNonce &&
              (await requestHandshakeFromOpener(oauth2Service, {
                nonce: callbackNonce,
              }))
            : await requestHandshakeFromOpener(oauth2Service);
          if (!hydrated) {
            sessionExpiredSignal.expire('missing-token');
            return;
          }
        }
        if (oauth2Service.oAuth2Token?.access_token) {
          outlookStore.setIsAuthorized(true);
        }
      } finally {
        initializationService.stopInitialization();
      }
    }),
    { provide: IS_BROWSER_ONLY_ROUTE, useValue: true },
    {
      provide: OAUTH2_PEER_TOKEN_BRIDGE,
      useValue: {
        tryGetTokenFromPeer: async () => null,
        broadcastTokenToPeers: () => undefined,
      },
    },
    provideCorePersonalAccessTokenDataAccess(),
    {
      provide: OUTLOOK_SYNC_ENV,
      useValue: { syncConfig: environment.syncConfig },
    },
  ],
};
